/**
 * Client mirror for shared bookings core.
 * R39 A16.a1 (plan docs/plans/sector-operations.md row SO-P74).
 * Pure code: pattern, dates, capacity, cancel.
 */

// ============================================================================
// 1. Pattern (mirrored from functions-common/src/bookings/pattern.ts)
// ============================================================================

export interface BookingSeriesPattern {
  weekdays: number[] // (0 = Sun … 6 = Sat)
  startTime: string // 'HH:MM'
  durationMin: number
  fromDate: string // 'YYYY-MM-DD'
  toDate: string // 'YYYY-MM-DD'
}
export type EduSeriesPattern = BookingSeriesPattern

export interface BookingHoliday {
  from: string // 'YYYY-MM-DD'
  to: string // 'YYYY-MM-DD'
  label?: string
}
export type EduHoliday = BookingHoliday

export interface BookingClashSlot {
  id?: string
  seriesId?: string
  weekdays: number[]
  startTime: string // 'HH:MM'
  durationMin: number
  fromDate: string // 'YYYY-MM-DD'
  toDate: string // 'YYYY-MM-DD'
  teacherUids?: string[]
  roomId?: string
  [key: string]: unknown
}
export type EduClashSlot = BookingClashSlot

export interface BookingClash {
  slotA: BookingClashSlot
  slotB: BookingClashSlot
  reason: 'TEACHER' | 'ROOM'
  teacherUid?: string
  roomId?: string
}
export type EduClash = BookingClash

export interface ValidatePatternResult {
  valid: boolean
  errors: string[]
}

function parseUtcDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d))
}

function formatUtcDate(date: Date): string {
  const y = date.getUTCFullYear()
  const m = String(date.getUTCMonth() + 1).padStart(2, '0')
  const d = String(date.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function getUtcWeekday(dateStr: string): number {
  return parseUtcDate(dateStr).getUTCDay()
}

function daysBetweenUtc(fromStr: string, toStr: string): number {
  const fromMs = parseUtcDate(fromStr).getTime()
  const toMs = parseUtcDate(toStr).getTime()
  return Math.round((toMs - fromMs) / (24 * 60 * 60 * 1000))
}

function addDaysUtc(dateStr: string, days: number): string {
  const dt = parseUtcDate(dateStr)
  dt.setUTCDate(dt.getUTCDate() + days)
  return formatUtcDate(dt)
}

function timeToMinutes(timeStr: string): number {
  const [h, m] = timeStr.split(':').map(Number)
  return h * 60 + m
}

export function sessionKey(seriesId: string, date: string): string {
  const yyyymmdd = date.replace(/-/g, '')
  return `${seriesId}_${yyyymmdd}`
}

export function bookingSessionDocId(bookableId: string, date: string): string {
  return sessionKey(bookableId, date)
}

export function isOccurrence(
  pattern: BookingSeriesPattern,
  date: string,
  holidays: BookingHoliday[] = [],
): boolean {
  if (date < pattern.fromDate || date > pattern.toDate) {
    return false
  }

  const weekday = getUtcWeekday(date)
  if (!pattern.weekdays.includes(weekday)) {
    return false
  }

  for (const h of holidays) {
    if (h.from <= date && date <= h.to) {
      return false
    }
  }

  return true
}

export function expandOccurrences(
  pattern: BookingSeriesPattern,
  holidays: BookingHoliday[] = [],
  windowFrom: string,
  windowTo: string,
): string[] {
  if (windowFrom > windowTo) {
    return []
  }

  let effectiveWindowTo = windowTo
  const windowDays = daysBetweenUtc(windowFrom, windowTo)
  if (windowDays > 120) {
    effectiveWindowTo = addDaysUtc(windowFrom, 120)
  }

  const start = pattern.fromDate > windowFrom ? pattern.fromDate : windowFrom
  const end = pattern.toDate < effectiveWindowTo ? pattern.toDate : effectiveWindowTo

  if (start > end) {
    return []
  }

  const result: string[] = []
  let curr = start
  while (curr <= end) {
    if (isOccurrence(pattern, curr, holidays)) {
      result.push(curr)
    }
    curr = addDaysUtc(curr, 1)
  }

  return result
}

export function findClashes(slots: BookingClashSlot[]): BookingClash[] {
  const clashes: BookingClash[] = []

  for (let i = 0; i < slots.length; i++) {
    for (let j = i + 1; j < slots.length; j++) {
      const a = slots[i]
      const b = slots[j]

      const hasSharedWeekday = a.weekdays.some((w) => b.weekdays.includes(w))
      if (!hasSharedWeekday) continue

      const maxFrom = a.fromDate > b.fromDate ? a.fromDate : b.fromDate
      const minTo = a.toDate < b.toDate ? a.toDate : b.toDate
      if (maxFrom > minTo) continue

      const aStart = timeToMinutes(a.startTime)
      const aEnd = aStart + a.durationMin
      const bStart = timeToMinutes(b.startTime)
      const bEnd = bStart + b.durationMin

      const maxStart = Math.max(aStart, bStart)
      const minEnd = Math.min(aEnd, bEnd)
      if (maxStart >= minEnd) continue

      const aTeachers = a.teacherUids || []
      const bTeachers = b.teacherUids || []
      const sharedTeachers = aTeachers.filter((t) => bTeachers.includes(t))
      for (const t of sharedTeachers) {
        clashes.push({
          slotA: a,
          slotB: b,
          reason: 'TEACHER',
          teacherUid: t,
        })
      }

      if (a.roomId && b.roomId && a.roomId === b.roomId) {
        clashes.push({
          slotA: a,
          slotB: b,
          reason: 'ROOM',
          roomId: a.roomId,
        })
      }
    }
  }

  return clashes
}

export function isValidIsoDate(s: unknown): s is string {
  if (typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return false
  }
  const [yearStr, monthStr, dayStr] = s.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  const day = Number(dayStr)
  const d = new Date(Date.UTC(year, month - 1, day))
  if (Number.isNaN(d.getTime())) {
    return false
  }
  return d.toISOString().slice(0, 10) === s
}

export function validatePattern(pattern: BookingSeriesPattern): ValidatePatternResult {
  const errors: string[] = []

  if (
    !Array.isArray(pattern.weekdays) ||
    pattern.weekdays.length === 0 ||
    pattern.weekdays.some((w) => typeof w !== 'number' || w < 0 || w > 6 || !Number.isInteger(w)) ||
    new Set(pattern.weekdays).size !== pattern.weekdays.length
  ) {
    errors.push('INVALID_WEEKDAYS')
  }

  if (
    typeof pattern.startTime !== 'string' ||
    !/^([01]\d|2[0-3]):[0-5]\d$/.test(pattern.startTime)
  ) {
    errors.push('INVALID_START_TIME')
  }

  if (
    typeof pattern.durationMin !== 'number' ||
    !Number.isFinite(pattern.durationMin) ||
    pattern.durationMin < 5 ||
    pattern.durationMin > 480
  ) {
    errors.push('INVALID_DURATION')
  }

  if (
    !isValidIsoDate(pattern.fromDate) ||
    !isValidIsoDate(pattern.toDate)
  ) {
    errors.push('INVALID_PATTERN')
  } else if (pattern.fromDate > pattern.toDate) {
    errors.push('INVALID_DATE_RANGE')
  } else {
    const span = daysBetweenUtc(pattern.fromDate, pattern.toDate)
    if (span > 400) {
      errors.push('SPAN_EXCEEDS_MAX')
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}

// ============================================================================
// 2. Dates (mirrored from functions-common/src/bookings/dates.ts)
// ============================================================================

export function parseYmdDate(ymd: string): { y: number; m: number; d: number } {
  const parts = ymd.split('-').map((p) => parseInt(p, 10))
  if (parts.length !== 3 || isNaN(parts[0]) || isNaN(parts[1]) || isNaN(parts[2])) {
    throw new Error(`Invalid date format: ${ymd}`)
  }
  return { y: parts[0], m: parts[1], d: parts[2] }
}

export function formatYmdDate(y: number, m: number, d: number): string {
  const mm = String(m).padStart(2, '0')
  const dd = String(d).padStart(2, '0')
  return `${y}-${mm}-${dd}`
}

export function addDaysYmd(ymd: string, days: number): string {
  const { y, m, d } = parseYmdDate(ymd)
  const dt = new Date(Date.UTC(y, m - 1, d + days))
  return formatYmdDate(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate())
}

export function addMonthsYmd(ymd: string, months: number): string {
  const { y, m, d } = parseYmdDate(ymd)
  const dt = new Date(Date.UTC(y, m - 1 + months, d))
  return formatYmdDate(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate())
}

export function diffDaysYmd(fromYmd: string, toYmd: string): number {
  const { y: y1, m: m1, d: d1 } = parseYmdDate(fromYmd)
  const { y: y2, m: m2, d: d2 } = parseYmdDate(toYmd)
  const dt1 = Date.UTC(y1, m1 - 1, d1)
  const dt2 = Date.UTC(y2, m2 - 1, d2)
  return Math.round((dt2 - dt1) / (24 * 60 * 60 * 1000))
}

export function getStartOfWeekYmd(ymd: string, timezone?: string | null): string {
  void timezone
  const { y, m, d } = parseYmdDate(ymd)
  const dt = new Date(Date.UTC(y, m - 1, d))
  const day = dt.getUTCDay() // 0 = Sun, 1 = Mon...
  const diff = day === 0 ? -6 : 1 - day // Mon start
  return addDaysYmd(ymd, diff)
}

export function getEndOfWeekYmd(ymd: string, timezone?: string | null): string {
  const start = getStartOfWeekYmd(ymd, timezone)
  return addDaysYmd(start, 6)
}

export function getStartOfMonthYmd(ymd: string, timezone?: string | null): string {
  void timezone
  const { y, m } = parseYmdDate(ymd)
  return formatYmdDate(y, m, 1)
}

export function getEndOfMonthYmd(ymd: string, timezone?: string | null): string {
  void timezone
  const { y, m } = parseYmdDate(ymd)
  const nextMonthFirst = new Date(Date.UTC(y, m, 1))
  const lastDay = new Date(nextMonthFirst.getTime() - 24 * 60 * 60 * 1000)
  return formatYmdDate(lastDay.getUTCFullYear(), lastDay.getUTCMonth() + 1, lastDay.getUTCDate())
}

export function getWeekBounds(ymd: string, timezone?: string | null): { start: string; end: string } {
  return {
    start: getStartOfWeekYmd(ymd, timezone),
    end: getEndOfWeekYmd(ymd, timezone),
  }
}

export function getMonthBounds(ymd: string, timezone?: string | null): { start: string; end: string } {
  return {
    start: getStartOfMonthYmd(ymd, timezone),
    end: getEndOfMonthYmd(ymd, timezone),
  }
}

export function hoursUntilSession(
  timezone?: string | null,
  date: string = '',
  startTime?: string | null,
  nowInput?: Date,
): number {
  const tz = typeof timezone === 'string' && timezone.trim() ? timezone.trim() : 'Asia/Kolkata'
  const effectiveTz = (() => {
    try {
      new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(new Date())
      return tz
    } catch {
      return 'Asia/Kolkata'
    }
  })()

  const now = nowInput || new Date()

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: effectiveTz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(now)

  let nowY = 0
  let nowM = 0
  let nowD = 0
  let nowHour = 0
  let nowMin = 0
  let nowSec = 0

  for (const p of parts) {
    if (p.type === 'year') nowY = parseInt(p.value, 10)
    else if (p.type === 'month') nowM = parseInt(p.value, 10)
    else if (p.type === 'day') nowD = parseInt(p.value, 10)
    else if (p.type === 'hour') {
      const parsed = parseInt(p.value, 10)
      nowHour = parsed === 24 ? 0 : parsed
    } else if (p.type === 'minute') nowMin = parseInt(p.value, 10)
    else if (p.type === 'second') nowSec = parseInt(p.value, 10)
  }

  const { y: sessY, m: sessM, d: sessD } = parseYmdDate(date || '1970-01-01')
  const [sessHStr, sessMinStr] = (startTime || '09:00').split(':')
  const sessH = parseInt(sessHStr, 10) || 0
  const sessMin = parseInt(sessMinStr, 10) || 0

  const sessWallMs = Date.UTC(sessY, sessM - 1, sessD, sessH, sessMin, 0)
  const nowWallMs = Date.UTC(nowY, nowM - 1, nowD, nowHour, nowMin, nowSec)

  return (sessWallMs - nowWallMs) / (1000 * 60 * 60)
}

export function computeSessionStartAt(
  date: string,
  startTime: string,
  timeZone?: string | null,
): Date {
  const tz = typeof timeZone === 'string' && timeZone.trim() ? timeZone.trim() : 'Asia/Kolkata'
  const { y, m, d } = parseYmdDate(date || '1970-01-01')
  const [hStr, minStr] = (startTime || '09:00').split(':')
  const hour = parseInt(hStr, 10) || 0
  const minute = parseInt(minStr, 10) || 0

  let guess = Date.UTC(y, m - 1, d, hour, minute, 0, 0)
  for (let i = 0; i < 3; i++) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false,
    })
    const parts = formatter.formatToParts(new Date(guess))
    const p: Record<string, number> = {}
    for (const part of parts) {
      if (part.type !== 'literal') p[part.type] = Number(part.value)
    }
    let pHour = p.hour ?? 0
    if (pHour === 24) pHour = 0
    const pUtc = Date.UTC(p.year ?? y, (p.month ?? m) - 1, p.day ?? d, pHour, p.minute ?? 0, p.second ?? 0, 0)
    const diff = pUtc - Date.UTC(y, m - 1, d, hour, minute, 0, 0)
    if (diff === 0) break
    guess -= diff
  }
  return new Date(guess)
}

export function getZonedTodayDate(
  timeZone?: string | null,
  nowInput?: Date,
): string {
  const tz = typeof timeZone === 'string' && timeZone.trim() ? timeZone.trim() : 'Asia/Kolkata'
  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    return formatter.format(nowInput || new Date())
  } catch {
    return (nowInput || new Date()).toISOString().slice(0, 10)
  }
}

// ============================================================================
// 3. Capacity (mirrored from functions-common/src/bookings/capacity.ts)
// ============================================================================

export const DEFAULT_CAPACITY = 30

export function seatsLeft(
  capacity: number | null | undefined,
  bookedCount: number | null | undefined,
): number {
  const cap = typeof capacity === 'number' && Number.isFinite(capacity) ? capacity : DEFAULT_CAPACITY
  const count = typeof bookedCount === 'number' && Number.isFinite(bookedCount) ? bookedCount : 0
  return Math.max(0, cap - count)
}

export function isSessionFull(
  capacity: number | null | undefined,
  bookedCount: number | null | undefined,
): boolean {
  const cap = typeof capacity === 'number' && Number.isFinite(capacity) ? capacity : DEFAULT_CAPACITY
  const count = typeof bookedCount === 'number' && Number.isFinite(bookedCount) ? bookedCount : 0
  return count >= cap
}

// ============================================================================
// 4. Cancel (mirrored from functions-common/src/bookings/cancel.ts)
// ============================================================================

export type CancelClassification = 'IN_WINDOW' | 'LATE'

export function classifyCancel(
  hoursUntil: number,
  cancelWindowHours: number,
): CancelClassification {
  return hoursUntil < cancelWindowHours ? 'LATE' : 'IN_WINDOW'
}

export function isLateCancel(
  hoursUntil: number,
  cancelWindowHours: number,
): boolean {
  return classifyCancel(hoursUntil, cancelWindowHours) === 'LATE'
}
